import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import tw, { styled } from 'twin.macro';

import { strings } from '../../localization/strings';
import CommonButtonDefault from '../common/buttons/default';
import OrdersFilterProps from '../../entities/ordersFilter';
import { OrderImportStatus, OrderStatus } from '../../entities/order';
import { useAuth } from '../../hooks/use-auth';
import * as sitesService from '../../services/api/sites';
import * as countriesService from '../../services/api/countries';
import { Site } from '../../entities/site';
import { Country } from '../../services/api/countries';

interface ComponentProps {
  filterState: OrdersFilterProps;
  onUpdate: (filter: OrdersFilterProps) => void;
}

const StyledBlock = styled.div`
  ${tw`items-center flex`};
`;
const StyledLabel = styled.label`
  ${tw`inline-block text-sm text-gray-500 mb-1`};
`;

export default function OrdersFilter({ filterState, onUpdate }: ComponentProps) {
  const { token } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);

  const initialState = {
    orderStatus: filterState.orderStatus && filterState ? filterState.orderStatus : 'all',
    importStatus: filterState.importStatus ? filterState.importStatus : OrderImportStatus.NoImport,
    textSearch: filterState.textSearch || '',
    siteId: filterState.siteId || '',
    countryId: filterState.countryId || '',
  };

  const emptyState = {
    orderStatus: 'all',
    importStatus: OrderImportStatus.NoImport,
    page: 1,
    textSearch: '',
    siteId: '',
    countryId: '',
  };

  const importStatusOptions = [
    { value: OrderImportStatus.NoImport, label: 'All' },
    { value: 'empty', label: 'No import' },
    { value: OrderImportStatus.Completed, label: 'Completed' },
  ];

  const orderStatusOptions = [
    { value: 'all', label: 'All' },
    { value: OrderStatus.New, label: 'Abandoned orders' },
    { value: OrderStatus.NotCompleted, label: 'Not completed' },
    { value: OrderStatus.Completed, label: 'Completed' },
    { value: OrderStatus.Cancelled, label: 'Cancelled' },
  ];

  const siteOptions = [
    { value: '', label: 'All' },
    ...sites.map((site) => ({
      value: site.id.toString(),
      label: site.domain,
    })),
  ];

  const countryOptions = [
    { value: '', label: 'All' },
    ...countries.map((country) => ({
      value: country.id.toString(),
      label: country.code,
    })),
  ];

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const data = await sitesService.list(token);

        setSites(data || []);
      } catch (e) {
        // Failed to fetch sites
      }
    };

    const fetchCountries = async () => {
      try {
        const data = await countriesService.list(token);

        setCountries(data || []);
      } catch (e) {
        // Failed to fetch countries
      }
    };

    fetchSites();
    fetchCountries();
  }, [token]);

  const [filter, setFilter] = useState<OrdersFilterProps>(initialState);
  // const [startDate, setStartDate] = useState(initialState.startDate);
  // const [endDate, setEndDate] = useState(initialState.endDate);

  const onImportStatusChange = (event: { value: string } | null) => {
    if (!event) return;
    setFilter({ ...filter, importStatus: event.value });
    onUpdate({ ...filter, importStatus: event.value });
  };

  const onOrderStatusChange = (event: { value: string } | null) => {
    if (!event) return;
    setFilter({ ...filter, orderStatus: event.value });
    onUpdate({ ...filter, orderStatus: event.value });
  };

  const onSiteChange = (event: { value: string } | null) => {
    if (!event) return;
    setFilter({ ...filter, siteId: event.value });
    onUpdate({ ...filter, siteId: event.value });
  };

  const onCountryChange = (event: { value: string } | null) => {
    if (!event) return;
    setFilter({ ...filter, countryId: event.value });
    onUpdate({ ...filter, countryId: event.value });
  };
  //
  // const onStartDateChange = (date: any) => {
  //   setStartDate(date);
  //
  //   date.setHours(0, 0, 0);
  //   endDate.setHours(23, 59, 0);
  //   setFilter({ ...filter, startDate: date, endDate });
  //   onUpdate({ ...filter, startDate: date, endDate });
  // };
  //
  // const onEndDateChange = (date: any) => {
  //   const end = date;
  //
  //   setEndDate(end);
  //   end.setHours(23, 59, 0);
  //
  //   setFilter({ ...filter, endDate: end });
  //   onUpdate({ ...filter, endDate: end });
  // };
  //
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onTextSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setFilter({ ...filter, textSearch: value });

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onUpdate({ ...filter, textSearch: value });
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const clearFilter = () => {
    // setStartDate(new Date());
    // setEndDate(new Date());
    setFilter(emptyState);
    onUpdate(emptyState);
  };

  return (
    <div className="mb-4 bg-white w-full shadow sm:rounded-lg">
      <div className="flex flex-wrap space-y-2 lg:space-y-0 px-4 py-4 space-x-4 items items-stretch">
        <StyledBlock className="w-72">
          <div className="w-full">
            <StyledLabel>{strings.filter.textSearch}:</StyledLabel>
            <input
              id="search"
              name="search"
              className="block w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm placeholder-gray-500 focus:outline-none focus:text-gray-900 focus:placeholder-gray-400 focus:ring-1 focus:ring-rose-500 focus:border-rose-500 sm:text-sm"
              placeholder="Search"
              type="search"
              onChange={(event) => onTextSearchChange(event)}
              value={filter.textSearch}
            />
          </div>
        </StyledBlock>
        <StyledBlock>
          <div>
            <StyledLabel htmlFor="orderStatus">{strings.filter.orderStatus}:</StyledLabel>

            <Select
              className="w-56 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              options={orderStatusOptions}
              value={orderStatusOptions.filter((option) => option.value === filter.orderStatus)}
              onChange={(event) => onOrderStatusChange(event)}
            />
          </div>
        </StyledBlock>

        <StyledBlock>
          <div>
            <StyledLabel htmlFor="importStatus">{strings.filter.importStatus}:</StyledLabel>

            <Select
              className="w-56 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              options={importStatusOptions}
              value={importStatusOptions.filter((option) => option.value === filter.importStatus)}
              onChange={(event) => onImportStatusChange(event)}
            />
          </div>
        </StyledBlock>
        <StyledBlock>
          <div>
            <StyledLabel htmlFor="site">{strings.filter.site}:</StyledLabel>

            <Select
              className="w-56 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              options={siteOptions}
              value={siteOptions.filter((option) => option.value === filter.siteId)}
              onChange={(event) => onSiteChange(event)}
            />
          </div>
        </StyledBlock>
        <StyledBlock>
          <div>
            <StyledLabel htmlFor="country">Country:</StyledLabel>

            <Select
              className="w-40 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              options={countryOptions}
              value={countryOptions.filter((option) => option.value === filter.countryId)}
              onChange={(event) => onCountryChange(event)}
            />
          </div>
        </StyledBlock>
        <StyledBlock className="!items-end">
          <CommonButtonDefault onClick={clearFilter} className="w-full md:w-auto inline-flex">
            {strings.filter.clearFilter}
          </CommonButtonDefault>
        </StyledBlock>
      </div>
    </div>
  );
}
